import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalRadii,
  signalShadows,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  LogoMark,
  Mono,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StepRail,
  Waveform,
} from '@/common/components/signal';
import { stemsWave } from '../fixtures';

const ONBOARDING_STEPS = ['01 HANDOFF', '02 FOLDER', '03 SCAN'];

/**
 * The panel all three states share. Solid rather than glass — these stand alone
 * on an empty canvas with nothing behind them to blur.
 */
const AuthPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      width: 452,
      maxWidth: '100%',
      borderRadius: `${signalRadii['3xl']}px`,
      border: `1px solid ${signalBorders.medium}`,
      background: signalSurfaces.panel,
      boxShadow: signalShadows.screen,
      padding: '34px 32px',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {children}
  </Box>
);

const PanelTitle: React.FC<{ size: number; children: React.ReactNode }> = ({
  size,
  children,
}) => (
  <Box
    component="h3"
    sx={{
      margin: '0 0 6px',
      fontFamily: signalFonts.display,
      fontWeight: 800,
      fontSize: `${size}px`,
      letterSpacing: '-0.03em',
      lineHeight: size >= 32 ? 1.02 : 1.05,
    }}
  >
    {children}
  </Box>
);

const PanelBody: React.FC<{ margin: string; children: React.ReactNode }> = ({
  margin,
  children,
}) => (
  <Box
    component="p"
    sx={{
      margin,
      fontSize: '13px',
      color: signalText.secondary,
      lineHeight: 1.6,
    }}
  >
    {children}
  </Box>
);

const InlineLink: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="a"
    href="#"
    sx={{
      color: signalColors.accentBright,
      fontWeight: 600,
      textDecoration: 'none',
    }}
  >
    {children}
  </Box>
);

/** Round status glyph in the handoff checklist. */
const StatusDot: React.FC<{ glyph: string; fg: string; bg: string }> = ({
  glyph,
  fg,
  bg,
}) => (
  <Box
    component="span"
    sx={{
      width: 20,
      height: 20,
      flex: 'none',
      borderRadius: '999px',
      background: bg,
      color: fg,
      fontSize: '11px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {glyph}
  </Box>
);

type Root = {
  path: string;
  meta: string;
  checked: boolean;
  logo?: string;
  daw?: string;
};

const roots: Root[] = [
  {
    path: '~/Music/Ableton',
    meta: '3 SESSIONS · 2.94 GB',
    checked: true,
    logo: signalDawLogos.Ableton,
    daw: 'Ableton',
  },
  {
    path: '~/Music/Logic',
    meta: '1 SESSION · 704 MB',
    checked: true,
    logo: signalDawLogos.LogicPro,
    daw: 'Logic Pro',
  },
  {
    path: '/Volumes/SSD-Sessions',
    meta: 'EXTERNAL · 41 SESSIONS · 210 GB',
    checked: false,
  },
];

/**
 * 06 — Auth & first run.
 *
 * Three consecutive moments, shown side by side: signing in, handing the
 * session from the browser back to the desktop app, and pointing the app at the
 * folders worth scanning. The middle panel is the one that usually goes
 * unspecified — a browser tab that has done its job and is waiting to be
 * replaced — so it states plainly what succeeded and what it is still waiting
 * on, rather than showing an unexplained spinner.
 */
export const Screen06Auth: React.FC = () => (
  <ScreenFrame
    index="06"
    title="Auth & first run"
    note="SIGN IN · DESKTOP HANDOFF · PICK FOLDER"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '22px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
      }}
    >
      <AuthPanel>
        <LogoMark size={26} sx={{ marginBottom: '30px' }} />
        <PanelTitle size={32}>Back to the session.</PanelTitle>
        <PanelBody margin="0 0 26px">
          Sign in to reach every version of every project.
        </PanelBody>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <SectionLabel size={10}>EMAIL</SectionLabel>
            <Box
              sx={{
                background: signalSurfaces.fill,
                border: `1px solid ${signalBorders.stronger}`,
                borderRadius: `${signalRadii.md}px`,
                padding: '11px 14px',
                fontSize: '14px',
              }}
            >
              mara@autocannon.fm
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <SectionLabel size={10}>PASSWORD</SectionLabel>
            {/* Focused field: accent border plus the focus ring. */}
            <Box
              sx={{
                background: 'rgba(255,106,31,0.06)',
                border: `1px solid ${signalColors.accent}`,
                borderRadius: `${signalRadii.md}px`,
                padding: '11px 14px',
                boxShadow: signalShadows.focusRing,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Mono
                size={14}
                weight={600}
                color={signalText.primary}
                letterSpacing="0.2em"
                sx={{ flex: 1 }}
              >
                ••••••••••
              </Mono>
              <Mono size={11}>SHOW</Mono>
            </Box>
          </Box>

          <SignalButton
            size="xl"
            sx={{
              padding: '12px 18px',
              marginTop: '4px',
              boxShadow: signalShadows.accentMd,
            }}
          >
            Sign in
          </SignalButton>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '4px 0',
            }}
          >
            <Box
              component="span"
              sx={{ flex: 1, height: '1px', background: signalBorders.default }}
            />
            <Mono size={10} color={signalText.faint}>
              OR
            </Mono>
            <Box
              component="span"
              sx={{ flex: 1, height: '1px', background: signalBorders.default }}
            />
          </Box>

          <SignalButton
            variant="secondary"
            sx={{
              padding: '11px 18px',
              background: signalSurfaces.fillHover,
              borderColor: signalBorders.stronger,
            }}
          >
            Continue with Google
          </SignalButton>
        </Box>

        <Box
          sx={{
            marginTop: 'auto',
            paddingTop: '26px',
            fontSize: '12.5px',
            color: signalText.muted,
          }}
        >
          No account yet? <InlineLink>Create one</InlineLink> — 500 MB free.
        </Box>
      </AuthPanel>

      <AuthPanel>
        <StepRail
          steps={ONBOARDING_STEPS}
          active={0}
          gap="8px"
          sx={{ marginBottom: '22px' }}
        />
        <PanelTitle size={28}>Finishing connection with StudioVault</PanelTitle>
        <PanelBody margin="0 0 24px">
          You signed in with Google in your browser. We&apos;re handing the
          session back to the desktop app — this window can close on its own.
        </PanelBody>

        <Box
          sx={{
            border: `1px solid ${signalStatus.syncing.border}`,
            background: 'rgba(255,106,31,0.07)',
            borderRadius: `${signalRadii['2xl']}px`,
            padding: '22px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <Waveform bars={stemsWave} height={34} barWidth={3} gap={3} />
          <Mono
            size={10.5}
            color={signalColors.accentBright}
            letterSpacing="0.1em"
          >
            studio-vault://auth/callback
          </Mono>
        </Box>

        <Box
          sx={{
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '13px',
              color: signalText.secondary,
            }}
          >
            <StatusDot
              glyph="✓"
              fg={signalColors.green}
              bg="rgba(62,207,142,0.15)"
            />
            Google account verified — mara@autocannon.fm
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '13px',
              color: signalText.secondary,
            }}
          >
            <StatusDot
              glyph="◴"
              fg={signalColors.accentBright}
              bg="rgba(255,106,31,0.15)"
            />
            Waiting for the app to take over…
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: 'auto',
            paddingTop: '26px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: '12.5px',
              color: signalText.muted,
              lineHeight: 1.5,
            }}
          >
            Not focused? <InlineLink>Try sign-in again</InlineLink>
          </Box>
          <SignalButton glow={false} sx={{ padding: '10px 18px' }}>
            Open StudioVault
          </SignalButton>
        </Box>
      </AuthPanel>

      <AuthPanel>
        <StepRail
          steps={ONBOARDING_STEPS}
          active={1}
          gap="8px"
          sx={{ marginBottom: '22px' }}
        />
        <PanelTitle size={28}>Where do you keep sessions?</PanelTitle>
        <PanelBody margin="0 0 22px">
          Pick one or more roots. We only read session folders — nothing else
          leaves your machine.
        </PanelBody>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {roots.map((root) => (
            <Box
              key={root.path}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: `1px solid ${
                  root.checked
                    ? signalBorders.accentHover
                    : signalBorders.default
                }`,
                background: root.checked
                  ? 'rgba(255,106,31,0.07)'
                  : 'transparent',
                borderRadius: '13px',
                padding: '13px 15px',
              }}
            >
              <Box
                component="span"
                role="checkbox"
                aria-checked={root.checked}
                aria-label={root.path}
                sx={{
                  width: 18,
                  height: 18,
                  flex: 'none',
                  borderRadius: `${signalRadii.xs}px`,
                  background: root.checked
                    ? signalColors.accent
                    : 'transparent',
                  border: root.checked ? 0 : '1px solid rgba(255,255,255,0.2)',
                  color: signalColors.onAccent,
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {root.checked ? '✓' : ''}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: root.checked
                      ? signalText.primary
                      : signalText.secondary,
                  }}
                >
                  {root.path}
                </Box>
                <Mono size={10} sx={{ display: 'block', marginTop: '3px' }}>
                  {root.meta}
                </Mono>
              </Box>

              {root.logo && (
                <DawTile
                  src={root.logo}
                  label={root.daw ?? ''}
                  size={26}
                  radius={7}
                />
              )}
            </Box>
          ))}

          <Box
            sx={{
              padding: '12px',
              borderRadius: '13px',
              border: `1px dashed ${signalBorders.dashed}`,
              color: signalText.muted,
              fontSize: '12.5px',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                color: signalText.primary,
                borderColor: 'rgba(255,255,255,0.28)',
              },
            }}
          >
            ＋ Add another folder
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: 'auto',
            paddingTop: '26px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Mono size={11} color={signalText.secondary}>
            2 ROOTS · 4 SESSIONS
          </Mono>
          <SignalButton glow={false} sx={{ padding: '10px 18px' }}>
            Scan folders
          </SignalButton>
        </Box>
      </AuthPanel>
    </Box>
  </ScreenFrame>
);

export default Screen06Auth;
